import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Bot, Zap, Clock, Settings, CheckCircle } from 'lucide-react';

const automationFeatures = [
  { icon: Zap, title: 'Smart Bidding', desc: 'AI adjusts bids hourly based on performance patterns' },
  { icon: Clock, title: 'Dayparting', desc: 'Automatically adjust budgets by time of day and week' },
  { icon: Settings, title: 'Keyword Harvesting', desc: 'Auto-promote converting search terms to targets' },
  { icon: CheckCircle, title: 'Negative Keyword Mining', desc: 'Automatically add underperforming terms as negatives' },
];

export default function AutomationSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.2 });

  return (
    <section
      ref={sectionRef}
      className="relative bg-[#05060B] grid-bg py-24 lg:py-32 overflow-hidden"
    >
      <div className="relative z-10 w-full px-6 lg:px-12 xl:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div 
              className={`order-2 lg:order-1 transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}
            >
              <div className="glass-card p-8">
                <div className="space-y-4">
                  {[
                    { step: 'Data Ingest', status: 'complete', time: 'Every 15 min' },
                    { step: 'Pattern Analysis', status: 'complete', time: 'Real-time' },
                    { step: 'Decision Engine', status: 'active', time: 'Processing...' },
                    { step: 'Bid Adjustment', status: 'pending', time: 'Next: 2:15 PM' },
                  ].map((item, index) => (
                    <div 
                      key={index}
                      className={`flex items-center gap-4 p-4 rounded-xl border ${
                        item.status === 'active' 
                          ? 'bg-cyan-500/10 border-cyan-500/30' 
                          : item.status === 'complete'
                          ? 'bg-dark-900/30 border-cyan-500/10'
                          : 'bg-dark-900/20 border-dark-800'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        item.status === 'complete' ? 'bg-green-500/20' :
                        item.status === 'active' ? 'bg-cyan-500/20 animate-pulse' :
                        'bg-dark-700'
                      }`}>
                        {item.status === 'complete' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : item.status === 'active' ? (
                          <Bot className="w-4 h-4 text-cyan-400" />
                        ) : (
                          <Clock className="w-4 h-4 text-dark-500" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white">{item.step}</div>
                        <div className="text-xs text-dark-500">{item.time}</div>
                      </div>
                      {item.status === 'active' && (
                        <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                      )}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-cyan-500/10">
                  {[
                    { label: 'Decisions/day', value: '2,400+' },
                    { label: 'Time Saved', value: '12 hrs' },
                    { label: 'Accuracy', value: '94%' },
                  ].map((stat, i) => (
                    <div key={i} className="text-center">
                      <div className="text-lg font-bold text-cyan-400">{stat.value}</div>
                      <div className="text-xs text-dark-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div 
              className={`order-1 lg:order-2 transition-all duration-1000 delay-200 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium uppercase tracking-wider mb-6">
                AI Automation
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Decisions made{' '}
                <span className="text-cyan-400 cyan-glow">automatically.</span>
              </h2>
              <p className="text-lg text-dark-400 mb-8">
                Our AI handles the routine decisions—bid adjustments, keyword management, budget allocation—so your team can focus on strategy.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {automationFeatures.map((feature, index) => (
                  <div 
                    key={index}
                    className="p-4 rounded-xl bg-dark-900/30 border border-cyan-500/10 hover:border-cyan-500/30 transition-colors"
                  >
                    <feature.icon className="w-6 h-6 text-cyan-400 mb-3" />
                    <h4 className="font-medium text-white mb-1">{feature.title}</h4>
                    <p className="text-sm text-dark-400">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
